import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Star, Facebook, Instagram, Youtube, Linkedin, Twitter } from "lucide-react";
import { COMPANY, AREAS, BOOKING_URL } from "@/data/site";

const airportLinks = ["Gatwick", "Heathrow", "Luton", "Stansted", "London City"];
const policies = [
  { label: "Business Accounts", to: "/business" },
  { label: "Terms & Conditions", to: "/policies#terms" },
  { label: "Privacy Policy", to: "/policies#privacy" },
  { label: "Cookies", to: "/policies#cookies" },
  { label: "Complaints", to: "/complaints" },
  { label: "Lost Property", to: "/lost-property" },
  { label: "Accessibility", to: "/policies#accessibility" },
];
const socials = [Facebook, Instagram, Youtube, Linkedin, Twitter];

export const Footer = () => {
  return (
    <footer data-testid="main-footer" className="bg-ink text-white/70">
      {/* trust strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex text-brand-light">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-brand-light" />)}</span>
            <span className="text-white font-semibold">4.9/5 · {COMPANY.reviews} Google reviews</span>
          </div>
          <a href={`tel:${COMPANY.phoneRaw}`} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-light text-white px-6 py-3 rounded-full font-bold transition-colors">
            <Phone className="h-4 w-4" /> {COMPANY.phone}
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-10 grid gap-12 md:grid-cols-12">
        <div className="md:col-span-4">
          <Link to="/" className="inline-block mb-6">
            <img
              src="/images/clocktower-logo-white.png"
              alt="Clocktower Cars Gatwick"
              width="929"
              height="224"
              className="h-16 md:h-20 w-auto"
            />
          </Link>
          <p className="text-white/50 leading-relaxed max-w-sm text-sm">
            {COMPANY.tagline}. Licensed, insured and available 24/7 across Gatwick, Crawley (RH10 & RH11) and Surrey.
          </p>
          <div className="flex gap-3 mt-6">
            {socials.map((Icon, i) => (
              <a key={i} href="#" aria-label="social link" className="grid place-items-center h-10 w-10 rounded-full border border-white/15 text-white/70 hover:bg-brand hover:border-brand hover:text-white transition-colors">
                <Icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-2">
          <h4 className="text-xs uppercase tracking-[0.2em] text-white font-bold mb-5">Airport Transfers</h4>
          <ul className="space-y-2.5 text-sm">
            {airportLinks.map((a) => (
              <li key={a}><a href={BOOKING_URL} className="text-white/50 hover:text-brand-light transition-colors">{a} Transfer</a></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.2em] text-white font-bold mb-5">Service Areas</h4>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            {AREAS.slice(0, 12).map((a) => (
              <li key={a.slug}><Link to={`/areas/${a.slug}`} className="text-white/50 hover:text-brand-light transition-colors">{a.name}</Link></li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-xs uppercase tracking-[0.2em] text-white font-bold mb-5">Contact</h4>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-brand-light shrink-0" strokeWidth={1.75} />
              <span className="text-white/60">{COMPANY.address}</span>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-brand-light shrink-0" strokeWidth={1.75} />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-brand-light text-white/60 break-all">{COMPANY.email}</a>
            </li>
            <li className="flex items-start gap-3">
              <Phone className="h-5 w-5 text-brand-light shrink-0" strokeWidth={1.75} />
              <a href={`tel:${COMPANY.phoneRaw}`} className="hover:text-brand-light text-white/60">{COMPANY.phone}</a>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between gap-4 text-sm text-white/40">
          <p>© {new Date().getFullYear()} Clocktower Cars Gatwick. All rights reserved.</p>
          <div className="flex flex-wrap gap-5">
            {policies.map((p) => (
              <Link key={p.label} to={p.to} data-testid={`footer-policy-${p.label.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="hover:text-white/80 transition-colors">
                {p.label}
              </Link>
            ))}
            <Link to="/admin" className="hover:text-white/70 transition-colors">Admin</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
